import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faEye,
  faFilePdf,
  faClipboardCheck,
  faRotateLeft,
  faCheck,
  faXmark,
  faGripVertical,
  faSignature,
  faStar,
  faTextWidth,
  faListCheck,
  faCircleDot,
  faSquareCheck,
  faAlignLeft
} from '@fortawesome/free-solid-svg-icons';
import api from '../api/axios';
import { toast } from 'react-toastify';
import ConfirmModal from './ConfirmModal';

const questionTypeIcons = {
  text: faTextWidth,
  textarea: faAlignLeft,
  checkbox: faSquareCheck,
  multi_checkbox: faListCheck,
  radio: faCircleDot,
  rating: faStar,
  signature: faSignature
};

const questionTypeLabels = {
  text: 'Text Input',
  textarea: 'Text Area',
  checkbox: 'Checkbox',
  multi_checkbox: 'Multi Checkbox',
  radio: 'Radio Button',
  rating: 'Rating Scale',
  signature: 'Signature'
};

const Feedback = ({ user }) => {
  const [activeTab, setActiveTab] = useState('cycles');
  const [templates, setTemplates] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Template builder state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '' });
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [sectionForm, setSectionForm] = useState({ section_title: '', section_description: '', display_order: 0 });
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'text',
    options: [],
    optionsInput: '', // Raw input string for options
    required: false,
    display_order: 0
  });
  const [selectedSection, setSelectedSection] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Cycle form state
  const [showCycleForm, setShowCycleForm] = useState(false);
  const [cycleForm, setCycleForm] = useState({
    project_id: '',
    template_id: '',
    cycle_start_date: '',
    cycle_end_date: '',
    due_date: ''
  });
  const [projects, setProjects] = useState([]);
  
  // Delete confirmations
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewCycle, setViewCycle] = useState(null);
  const [viewCycleLoading, setViewCycleLoading] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const isBDM = user?.role === 'BDM';

  const getBackendBaseUrl = () => 'http://localhost:5000';

  const openViewCycle = async (cycle) => {
    setViewCycle(cycle);
    try {
      setViewCycleLoading(true);
      const res = await api.get(`/api/feedback/cycles/${cycle.id}`);
      setViewCycle(prev => ({
        ...(prev || {}),
        ...res.data
      }));
    } catch (err) {
      toast.error('Failed to load feedback details');
    } finally {
      setViewCycleLoading(false);
    }
  };

  const getSignatureSrc = (signatureFile) => {
    if (!signatureFile) return '';
    if (typeof signatureFile !== 'string') return '';
    if (signatureFile.startsWith('data:image')) return signatureFile;
    if (signatureFile.startsWith('http://') || signatureFile.startsWith('https://')) return signatureFile;
    if (signatureFile.startsWith('/')) return `${getBackendBaseUrl()}${signatureFile}`;
    return `${getBackendBaseUrl()}/${signatureFile}`;
  };

  const openOrDownloadPdf = async (cycleId, mode = 'download') => {
    try {
      const res = await api.get(`/api/feedback/cycles/${cycleId}/pdf/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (mode === 'view') {
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }

      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback_${cycleId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download PDF');
    }
  };

  useEffect(() => {
    if (!viewCycle) return undefined;

    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [viewCycle]);

  // Fetch cycles
  const fetchCycles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/feedback/cycles');
      setCycles(res.data.items || res.data);
    } catch (err) {
      toast.error('Failed to load feedback cycles');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/feedback/templates');
      setTemplates(res.data);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch projects for cycle creation
  const fetchProjects = useCallback(async () => {
    try {
      const res = await api.get('/api/projects?all=true');
      setProjects(res.data);
    } catch (err) {
      console.error('Failed to load projects');
    }
  }, []);

  useEffect(() => {
    fetchCycles();
    fetchTemplates(); // Templates needed for all users to see cycle details
    if (isAdmin || isBDM) {
      fetchProjects(); // Only ADMIN and BDM need projects list to create cycles
    }
  }, [fetchCycles, fetchTemplates, fetchProjects, isAdmin, isBDM]);

  // Template operations
  const createTemplate = async () => {
    try {
      await api.post('/api/feedback/templates', templateForm);
      toast.success('Template created');
      setShowTemplateForm(false);
      setTemplateForm({ name: '', description: '' });
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create template');
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/api/feedback/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete template');
    }
  };

  const loadTemplate = async (id) => {
    try {
      const res = await api.get(`/api/feedback/templates/${id}?full=true`);
      setSelectedTemplate(res.data);
    } catch (err) {
      toast.error('Failed to load template');
    }
  };

  // Section operations
  const createSection = async () => {
    try {
      await api.post(`/api/feedback/templates/${selectedTemplate.id}/sections`, sectionForm);
      toast.success('Section added');
      setShowSectionForm(false);
      setSectionForm({ section_title: '', section_description: '', display_order: 0 });
      loadTemplate(selectedTemplate.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create section');
    }
  };

  const deleteSection = async (id) => {
    try {
      await api.delete(`/api/feedback/sections/${id}`);
      toast.success('Section deleted');
      loadTemplate(selectedTemplate.id);
    } catch (err) {
      toast.error('Failed to delete section');
    }
  };

  // Question operations
  const createQuestion = async () => {
    try {
      // Parse options from the input string when for radio/multi_checkbox types
      const shouldHaveOptions = ['radio', 'multi_checkbox'].includes(questionForm.question_type);
      let parsedOptions = null;
      if (shouldHaveOptions && questionForm.optionsInput) {
        // Split by newlines first, then by commas/semicolons within each line
        const lines = questionForm.optionsInput.split(/\n/);
        parsedOptions = lines
          .flatMap(line => line.split(/[,;]/))
          .map(o => o.trim())
          .filter(o => o);
      }
      
      const payload = {
        ...questionForm,
        options: parsedOptions
      };
      
      if (editingQuestion) {
        await api.put(`/api/feedback/questions/${editingQuestion.id}`, payload);
        toast.success('Question updated');
        setEditingQuestion(null);
      } else {
        await api.post(`/api/feedback/sections/${selectedSection}/questions`, payload);
        toast.success('Question added');
      }
      
      setShowQuestionForm(false);
      setEditingQuestion(null);
      setQuestionForm({
        question_text: '',
        question_type: 'text',
        options: [],
        optionsInput: '',
        required: false,
        display_order: 0
      });
      loadTemplate(selectedTemplate.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save question');
    }
  };

  const deleteQuestion = async (id) => {
    try {
      await api.delete(`/api/feedback/questions/${id}`);
      toast.success('Question deleted');
      loadTemplate(selectedTemplate.id);
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  const startEditQuestion = (question) => {
    setEditingQuestion(question);
    const opts = question.options || [];
    setQuestionForm({
      question_text: question.question_text,
      question_type: question.question_type,
      options: opts,
      optionsInput: Array.isArray(opts) ? opts.join(', ') : '',
      required: question.required,
      display_order: question.display_order
    });
    setShowQuestionForm(true);
  };

  // Cycle operations
  const createCycle = async () => {
    try {
      await api.post('/api/feedback/cycles', cycleForm);
      toast.success('Feedback cycle created');
      setShowCycleForm(false);
      setCycleForm({
        project_id: '',
        template_id: '',
        cycle_start_date: '',
        cycle_end_date: '',
        due_date: ''
      });
      fetchCycles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create cycle');
    }
  };

  const deleteCycle = async (id) => {
    try {
      await api.delete(`/api/feedback/cycles/${id}`);
      toast.success('Cycle deleted');
      fetchCycles();
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete cycle');
    }
  };

  const generatePdf = async (cycleId) => {
    try {
      const res = await api.get(`/api/feedback/cycles/${cycleId}/pdf`);
      toast.success('PDF generated');
      await openOrDownloadPdf(cycleId, 'download');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate PDF');
    }
  };

  const closeCycle = async (cycleId) => {
    try {
      await api.put(`/api/feedback/cycles/${cycleId}`, { status: 'CLOSED' });
      toast.success('Cycle closed');
      fetchCycles();
    } catch (err) {
      toast.error('Failed to close cycle');
    }
  };

  // Render functions
  const renderTemplateBuilder = () => (
    <div className="feedback-template-builder">
      <div className="feedback-toolbar">
        <h2>Template Builder</h2>
        <button className="btn btn-primary" onClick={() => setShowTemplateForm(true)}>
          <FontAwesomeIcon icon={faPlus} /> New Template
        </button>
      </div>

      {!selectedTemplate ? (
        <div className="templates-list">
          {templates.map(template => (
            <div key={template.id} className="template-card" onClick={() => loadTemplate(template.id)}>
              <div className="template-card-header">
                <h3>{template.name}</h3>
                <span className={`badge ${template.status.toLowerCase()}`}>{template.status}</span>
              </div>
              <p className="template-description">{template.description}</p>
              <div className="template-meta">
                <span>v{template.version}</span>
                <span>{template.section_count || 0} sections</span>
                <span>{template.question_count || 0} questions</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="template-editor">
          <div className="template-editor-header">
            <button className="btn btn-link" onClick={() => setSelectedTemplate(null)}>
              <FontAwesomeIcon icon={faRotateLeft} /> Back to Templates
            </button>
            <h2>{selectedTemplate.name}</h2>
            <p>{selectedTemplate.description}</p>
          </div>

          <div className="sections-container">
            <div className="section-toolbar">
              <button className="btn btn-secondary" onClick={() => setShowSectionForm(true)}>
                <FontAwesomeIcon icon={faPlus} /> Add Section
              </button>
            </div>

            {selectedTemplate.sections?.map(section => (
              <div key={section.id} className="feedback-section">
                <div className="section-header">
                  <h3>{section.section_title}</h3>
                  <div className="section-actions">
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => {
                        setSelectedSection(section.id);
                        setShowQuestionForm(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Question
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteSection(section.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
                {section.section_description && (
                  <p className="section-description">{section.section_description}</p>
                )}

                <div className="questions-list">
                  {section.questions?.map((q, idx) => (
                    <div key={q.id} className="question-item">
                      <span className="question-number">{idx + 1}</span>
                      <FontAwesomeIcon icon={questionTypeIcons[q.question_type]} className="question-type-icon" />
                      <span className="question-text">{q.question_text}</span>
                      {q.required && <span className="required-badge">*</span>}
                      <div className="question-actions">
                        <button className="btn btn-sm" onClick={() => startEditQuestion(q)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteQuestion(q.id)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderCyclesDashboard = () => (
    <div className="feedback-cycles-dashboard">
      <div className="feedback-toolbar">
        <h2>Feedback Cycles</h2>
        {(isAdmin || isBDM) && (
          <button className="btn btn-primary" onClick={() => setShowCycleForm(true)}>
            <FontAwesomeIcon icon={faPlus} /> New Cycle
          </button>
        )}
      </div>

      <div className="cycles-list">
        {cycles.map(cycle => (
          <div key={cycle.id} className={`cycle-card status-${cycle.status.toLowerCase()}`}>
            <div className="cycle-header">
              <div className="cycle-title">
                <h3>{cycle.project_title}</h3>
                <span className={`status-badge ${cycle.status.toLowerCase()}`}>{cycle.status}</span>
              </div>
              <div className="cycle-actions">
                <button className="btn btn-sm" onClick={() => openViewCycle(cycle)}>
                  <FontAwesomeIcon icon={faEye} />
                </button>
                {cycle.pdf_file && (
                  <button className="btn btn-sm btn-secondary" onClick={() => openOrDownloadPdf(cycle.id, 'download')}>
                    <FontAwesomeIcon icon={faFilePdf} />
                  </button>
                )}
                {(isAdmin || isBDM) && cycle.status === 'SUBMITTED' && (
                  <>
                    <button className="btn btn-sm btn-secondary" onClick={() => generatePdf(cycle.id)}>
                      <FontAwesomeIcon icon={faFilePdf} /> Generate PDF
                    </button>
                    <button className="btn btn-sm btn-success" onClick={() => closeCycle(cycle.id)}>
                      <FontAwesomeIcon icon={faCheck} /> Close
                    </button>
                  </>
                )}
                {(isAdmin || isBDM) && cycle.status === 'PENDING' && (
                  <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm({ id: cycle.id, name: `Cycle for ${cycle.project_title}` })}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </div>
            <div className="cycle-details">
              <div className="cycle-detail">
                <span className="label">Client:</span>
                <span className="value">{cycle.client_name} {cycle.client_company && `(${cycle.client_company})`}</span>
              </div>
              <div className="cycle-detail">
                <span className="label">Template:</span>
                <span className="value">{cycle.template_name}</span>
              </div>
              <div className="cycle-detail">
                <span className="label">Period:</span>
                <span className="value">
                  {new Date(cycle.cycle_start_date).toLocaleDateString()} - {new Date(cycle.cycle_end_date).toLocaleDateString()}
                </span>
              </div>
              {cycle.due_date && (
                <div className="cycle-detail">
                  <span className="label">Due:</span>
                  <span className={`value ${new Date(cycle.due_date) < new Date() && cycle.status === 'PENDING' ? 'overdue' : ''}`}>
                    {new Date(cycle.due_date).toLocaleDateString()}
                  </span>
                </div>
              )}
              {cycle.response_count !== undefined && (
                <div className="cycle-detail">
                  <span className="label">Responses:</span>
                  <span className="value">{cycle.response_count} answered</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="feedback-page">
      <div className="page-header">
        <h1>Feedback & Service Assurance</h1>
        <p>Manage client feedback forms and service quality assurance cycles</p>
      </div>

      <div className="feedback-tabs">
        <button 
          className={`tab-btn ${activeTab === 'cycles' ? 'active' : ''}`}
          onClick={() => setActiveTab('cycles')}
        >
          <FontAwesomeIcon icon={faClipboardCheck} /> Cycles
        </button>
        {(isAdmin || isBDM) && (
          <button 
            className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => setActiveTab('templates')}
          >
            <FontAwesomeIcon icon={faClipboardCheck} /> Templates
          </button>
        )}
      </div>

      <div className="feedback-content">
        {activeTab === 'cycles' ? renderCyclesDashboard() : renderTemplateBuilder()}
      </div>

      {/* Template Form Modal */}
      {showTemplateForm && (
        <div className="modal-overlay" onClick={() => setShowTemplateForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Template</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Template Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={templateForm.name}
                  onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Quarterly Service Feedback"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="glass-textarea"
                  value={templateForm.description}
                  onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="Brief description of this feedback template"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="glass-button" onClick={() => setShowTemplateForm(false)}>Cancel</button>
              <button className="glass-button primary" onClick={createTemplate} disabled={!templateForm.name.trim()}>
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Form Modal */}
      {showSectionForm && (
        <div className="modal-overlay" onClick={() => setShowSectionForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Section</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Section Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={sectionForm.section_title}
                  onChange={e => setSectionForm({ ...sectionForm, section_title: e.target.value })}
                  placeholder="e.g., Service Delivery Experience"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="glass-textarea"
                  value={sectionForm.section_description}
                  onChange={e => setSectionForm({ ...sectionForm, section_description: e.target.value })}
                  placeholder="Optional description for this section"
                  rows={2}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="glass-button" onClick={() => setShowSectionForm(false)}>Cancel</button>
              <button className="glass-button primary" onClick={createSection} disabled={!sectionForm.section_title.trim()}>
                Add Section
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Question Form Modal */}
      {showQuestionForm && (
        <div className="modal-overlay" onClick={() => {
          setShowQuestionForm(false);
          setEditingQuestion(null);
        }}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingQuestion ? 'Edit Question' : 'Add Question'}</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Question Text *</label>
                <input
                  type="text"
                  className="form-input"
                  value={questionForm.question_text}
                  onChange={e => setQuestionForm({ ...questionForm, question_text: e.target.value })}
                  placeholder="Enter your question"
                />
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'end' }}>
                <div className="form-group">
                  <label className="form-label">Question Type</label>
                  <select
                    className="glass-select w-full"
                    value={questionForm.question_type}
                    onChange={e => setQuestionForm({ ...questionForm, question_type: e.target.value })}
                  >
                    {Object.entries(questionTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingBottom: '12px' }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={questionForm.required}
                      onChange={e => setQuestionForm({ ...questionForm, required: e.target.checked })}
                    />
                    Required
                  </label>
                </div>
              </div>

              {['radio', 'multi_checkbox'].includes(questionForm.question_type) && (
                <div className="form-group">
                  <label className="form-label">Options (one per line or comma-separated)</label>
                  <textarea
                    className="glass-textarea"
                    defaultValue={questionForm.optionsInput}
                    onBlur={e => setQuestionForm({ ...questionForm, optionsInput: e.target.value })}
                    placeholder="yes&#10;no"
                    rows={3}
                    style={{ minHeight: '80px' }}
                  />
                  <small style={{ color: 'var(--text-tertiary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Enter each option on a new line, or separate with commas
                  </small>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="glass-button" onClick={() => {
                setShowQuestionForm(false);
                setEditingQuestion(null);
              }}>Cancel</button>
              <button className="glass-button primary" onClick={createQuestion} disabled={!questionForm.question_text.trim()}>
                {editingQuestion ? 'Update Question' : 'Add Question'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cycle Form Modal */}
      {showCycleForm && (
        <div className="modal-overlay" onClick={() => setShowCycleForm(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Feedback Cycle</h3>
            </div>
            <div className="modal-body">
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Project *</label>
                  <select
                    className="glass-select w-full"
                    value={cycleForm.project_id}
                    onChange={e => setCycleForm({ ...cycleForm, project_id: e.target.value })}
                  >
                    <option value="">Select Project</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Template *</label>
                  <select
                    className="glass-select w-full"
                    value={cycleForm.template_id}
                    onChange={e => setCycleForm({ ...cycleForm, template_id: e.target.value })}
                  >
                    <option value="">Select Template</option>
                    {templates.filter(t => t.status === 'ACTIVE').map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Start Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={cycleForm.cycle_start_date}
                    onChange={e => setCycleForm({ ...cycleForm, cycle_start_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={cycleForm.cycle_end_date}
                    onChange={e => setCycleForm({ ...cycleForm, cycle_end_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={cycleForm.due_date}
                    onChange={e => setCycleForm({ ...cycleForm, due_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="glass-button" onClick={() => setShowCycleForm(false)}>Cancel</button>
              <button 
                className="glass-button primary" 
                onClick={createCycle}
                disabled={!cycleForm.project_id || !cycleForm.template_id || !cycleForm.cycle_start_date || !cycleForm.cycle_end_date}
              >
                Create Cycle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Cycle Modal */}
      {viewCycle && (
        <div className="modal-overlay" onClick={() => setViewCycle(null)}>
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Feedback Details - {viewCycle.project_title}</h3>
              <button className="btn btn-sm" onClick={() => setViewCycle(null)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="cycle-view-content">
              <div className="cycle-info-grid">
                <div className="info-item">
                  <span className="label">Client:</span>
                  <span className="value">{viewCycle.client_name}</span>
                </div>
                <div className="info-item">
                  <span className="label">Template:</span>
                  <span className="value">{viewCycle.template_name}</span>
                </div>
                <div className="info-item">
                  <span className="label">Status:</span>
                  <span className={`value status-${viewCycle.status.toLowerCase()}`}>{viewCycle.status}</span>
                </div>
                <div className="info-item">
                  <span className="label">Period:</span>
                  <span className="value">
                    {new Date(viewCycle.cycle_start_date).toLocaleDateString()} - {new Date(viewCycle.cycle_end_date).toLocaleDateString()}
                  </span>
                </div>
                {viewCycle.due_date && (
                  <div className="info-item">
                    <span className="label">Due:</span>
                    <span className="value">{new Date(viewCycle.due_date).toLocaleDateString()}</span>
                  </div>
                )}
                {viewCycle.submitted_at && (
                  <div className="info-item">
                    <span className="label">Submitted:</span>
                    <span className="value">{new Date(viewCycle.submitted_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {viewCycleLoading ? (
                <div style={{ padding: 16 }}>
                  Loading submitted details...
                </div>
              ) : viewCycle.sections && (
                <div className="cycle-responses">
                  {viewCycle.sections.map(section => (
                    <div key={section.id} className="response-section">
                      <h4>{section.section_title}</h4>
                      {section.section_description && <p>{section.section_description}</p>}
                      <div className="responses-list">
                        {section.questions.map((q, idx) => (
                          <div key={q.id} className="response-item">
                            <div className="response-question">
                              <span className="q-number">{idx + 1}.</span>
                              {q.question_text}
                              {q.required && <span className="required">*</span>}
                            </div>
                            <div className="response-answer">
                              {q.response ? (
                                <>
                                  {q.question_type === 'rating' && (
                                    <div className="rating-display">
                                      {(() => {
                                        const ratingNum = Math.max(
                                          0,
                                          Math.min(5, Number(q.response?.rating || 0))
                                        );
                                        return (
                                          <>
                                            {'★'.repeat(ratingNum)}
                                            {'☆'.repeat(5 - ratingNum)}
                                            <span style={{ marginLeft: 8, opacity: 0.8 }}>{ratingNum}/5</span>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                                  {q.question_type === 'checkbox' && (
                                    <span>{q.response.checked ? 'Yes' : 'No'}</span>
                                  )}
                                  {(q.question_type === 'text' || q.question_type === 'textarea') && (
                                    <p>{q.response.text}</p>
                                  )}
                                  {(q.question_type === 'radio' || q.question_type === 'multi_checkbox') && (
                                    <span>{Array.isArray(q.response.selected) ? q.response.selected.join(', ') : q.response.selected}</span>
                                  )}
                                </>
                              ) : (
                                <span className="no-response">No response</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {viewCycle.signature_file && (
                <div className="signature-section">
                  <h4>Digital Signature</h4>
                  <div className="signature-info">
                    <p><strong>Representative:</strong> {viewCycle.representative_name}</p>
                    {viewCycle.designation && <p><strong>Designation:</strong> {viewCycle.designation}</p>}
                    <p><strong>Signed on:</strong> {new Date(viewCycle.signed_at).toLocaleString()}</p>
                  </div>
                  <img src={getSignatureSrc(viewCycle.signature_file)} alt="Digital signature" className="signature-image" />
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="glass-button" onClick={() => setViewCycle(null)}>Close</button>
              {(viewCycle.status === 'SUBMITTED' || viewCycle.status === 'CLOSED') && (
                <>
                  {viewCycle.pdf_file ? (
                    <button className="glass-button primary" onClick={() => openOrDownloadPdf(viewCycle.id, 'download')}>
                      <FontAwesomeIcon icon={faFilePdf} /> Download PDF
                    </button>
                  ) : (
                    (isAdmin || isBDM) && (
                      <button className="glass-button primary" onClick={() => generatePdf(viewCycle.id)}>
                        <FontAwesomeIcon icon={faFilePdf} /> Generate PDF
                      </button>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            if (activeTab === 'templates') {
              deleteTemplate(deleteConfirm.id);
            } else {
              deleteCycle(deleteConfirm.id);
            }
          }
        }}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        danger
      />

      {loading && (
        <div className="loading-overlay">
          <div className="spinner" />
        </div>
      )}
    </div>
  );
};

export default Feedback;
